import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useUpdateDesignationMutation } from 'redux/features/designationApi';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EditDesignationModal = ({ isOpen, onClose, designation, onDesignationUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    overtime_enabled: false,
    paid_leave: '',
  });

  const [originalData, setOriginalData] = useState({});

  const [updateDesignation, { isLoading }] = useUpdateDesignationMutation();

  useEffect(() => {
    if (designation && isOpen) {
      const data = {
        name: designation.name || '',
        overtime_enabled: Boolean(designation.overtime_enabled),
        paid_leave: designation.paid_leave?.toString() || '',
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [designation, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const hasChanges = useCallback(() => {
    return (
      formData.name.trim() !== originalData.name.trim() ||
      formData.overtime_enabled !== originalData.overtime_enabled ||
      formData.paid_leave !== originalData.paid_leave
    );
  }, [formData, originalData]);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData.name?.trim()) {
      toast.error('Please enter a designation name');
      return;
    }

    if (!designation?.id) {
      toast.error('Designation information is missing');
      return;
    }

    try {
      const payload = {
        id: designation.id,
        name: formData.name.trim(),
        department_id:
          designation.departmentId || designation.department_id || designation.department?.id,
        overtime_enabled: formData.overtime_enabled,
        paid_leave: parseInt(formData.paid_leave) || 0,
      };

      const result = await updateDesignation(payload).unwrap();
      toast.success(`Designation "${formData.name.trim()}" updated successfully!`);
      onDesignationUpdated?.(result);
      onClose();
    } catch (error) {
      console.error('Error updating designation:', error);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', overtime_enabled: false, paid_leave: '' });
    setOriginalData({});
    onClose();
  };

  if (!isOpen || !designation) return null;

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center overflow-y-auto bg-gray-500 bg-opacity-75 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-primary-100 p-2">
                <Icon name="Pencil" size={20} className="text-primary-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Edit Designation</h3>
                <p className="text-sm text-gray-500">Update role details</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Department Info */}
        <div className="bg-blue-50 border-b border-blue-100 px-6 py-4">
          <div className="flex items-center space-x-2 text-sm text-blue-800">
            <Icon name="Building2" size={16} />
            <span>Department:</span>
            <strong className="font-medium capitalize">
              {designation.department?.name || designation.department_name || 'N/A'}
            </strong>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Designation Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed hover:border-gray-300 transition-colors"
              placeholder="e.g., Software Engineer"
              required
              disabled={isLoading}
            />
            <p className="mt-1.5 text-xs text-gray-500">Must be unique within the department</p>
          </div>

          {/* Settings */}
          <fieldset disabled={isLoading}>
            <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-inner">
              <legend className="px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Settings
              </legend>
              <div className="space-y-4 pt-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Overtime Eligible
                  </label>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-900 font-medium">
                      {formData.overtime_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <label
                      className={`relative inline-flex h-6 w-11 items-center rounded-full ${formData.overtime_enabled ? 'bg-blue-500' : 'bg-gray-200'}  transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:cursor-not-allowed`}
                    >
                      <input
                        type="checkbox"
                        name="overtime_enabled"
                        checked={formData.overtime_enabled}
                        onChange={(e) =>
                          handleInputChange({
                            target: { name: 'overtime_enabled', value: e.target.checked },
                          })
                        }
                        className="sr-only peer"
                        disabled={isLoading}
                      />
                      <span className="pointer-events-none absolute mx-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition peer-checked:translate-x-5 peer-checked:bg-white peer-focus:ring-primary-400" />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Paid Leave Days (Monthly)
                  </label>
                  <div className="relative flex items-center">
                    <Icon
                      name="CalendarDays"
                      className="absolute left-3 h-5 w-5 text-gray-400 pointer-events-none"
                    />
                    <input
                      type="number"
                      name="paid_leave"
                      value={formData.paid_leave}
                      onChange={handleInputChange}
                      min="0"
                      max="31"
                      step="1"
                      className="w-full rounded-md border border-gray-200 bg-white pl-11 pr-4 py-3 text-sm shadow-sm placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed hover:border-gray-300 transition-colors"
                      placeholder="e.g., 2"
                      disabled={isLoading}
                    />
                    <span className="absolute right-8 text-xs text-gray-500 font-medium pointer-events-none">
                      days
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          {/* Buttons */}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-0 sm:flex-none sm:w-auto transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={isLoading || !formData.name?.trim() || !hasChanges()}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto transition-all"
              loading={isLoading}
            >
              <Icon name="Check" size={16} />
              Update Designation
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDesignationModal;
